import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { withTheme } from '@mui/styles';
import { useTranslation } from 'next-i18next';
import { useInterval } from 'react-use';

import IntroductionSectionStyle from './style';

import FrameGroup from 'src/components/organisms/b2bLandingPage/frameGroup';
import { useWindowSize } from 'src/helpers/use';

const FRAME_DATA_GROUP = [
  [
    {
      icon: '/static/icons/book-play.svg',
      tName: 'quality_online_course',
    },
    {
      icon: '/static/icons/people.svg',
      tName: 'develop_content_for_organization',
    },
    {
      icon: '/static/icons/bookmark.svg',
      tName: 'completely_content_and_categories',
    },
    {
      icon: '/static/icons/book-open.svg',
      tName: 'summary_book_in_seven_minute',
    },
  ],
  [
    {
      icon: '/static/icons/device.svg',
      tName: 'learn_any_time',
    },
    {
      icon: '/static/icons/website.svg',
      tName: 'management_system',
    },
    {
      icon: '/static/icons/book-track.svg',
      tName: 'track_and_evaluate',
    },
    {
      icon: '/static/icons/website-dashboard.svg',
      tName: 'learning_dashboard',
    },
  ],
  [
    {
      icon: '/static/icons/consult.svg',
      tName: 'consult_team',
    },
    {
      icon: '/static/icons/communication.svg',
      tName: 'communicate_in_organization',
    },
    {
      icon: '/static/icons/evaluate-satisfy.svg',
      tName: 'evaluation_tool',
    },
    {
      icon: '/static/icons/production.svg',
      tName: 'professional_team',
    },
  ],
];

const between = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

type MoveUpPropType = {
  children: object;
  ratio: number;
  theme: any;
};
const MoveUp = withTheme(({ children, ratio, theme }: MoveUpPropType) => {
  const { width = 0 } = useWindowSize();

  let gap = 0;
  if (width < theme.breakpoints.values.sm) {
    gap = 30;
  } else if (width < theme.breakpoints.values.lg) {
    gap = 80;
  } else if (width > theme.breakpoints.values.lg) {
    gap = 150;
  }

  const START_POSITION = 180 + gap;
  const CENTER_POSITION = 30 + gap;
  const END_POSITION = 0 + gap;
  let opacity = 0;

  const length = START_POSITION - CENTER_POSITION;
  let top = length * (1 - ratio) + CENTER_POSITION;
  if (ratio <= 0.2) {
    opacity = ratio;
  } else if (ratio > 0.2 && ratio < 0.8) {
    opacity = 1;
  } else if (ratio >= 0.8) {
    opacity = 1 - ratio;
  }
  top = between(top, END_POSITION, START_POSITION);
  return <div style={{ opacity, top, position: 'absolute' }}>{children}</div>;
});

const IntroductionSection = () => {
  const { t } = useTranslation('business');
  const [bannerIndex, setBannerIndex] = useState(0);
  const [scrollRatio, setScrollRatio] = useState(0);
  const [initRatio, setInitRatio] = useState(0);
  const [delay, setDelay] = useState<number | null>(15);

  const handleNavigation = () => {
    const { scrollTop } = document.documentElement;
    const viewportHeight = window.innerHeight;
    const scrollPerPage = scrollTop / viewportHeight;
    const index = Math.max(Math.round(scrollPerPage), 0);
    const ratio = index < 1 ? scrollPerPage : scrollPerPage - index + 0.5;

    // banner amount is 3
    setBannerIndex(Math.min(index, 2));
    setScrollRatio(index <= 2 ? ratio : 1);
  };

  useInterval(() => {
    if (initRatio >= 0.5) setDelay(null);
    setInitRatio(initRatio + 0.01);
  }, delay);

  useEffect(() => {
    if (window) window.addEventListener('scroll', () => handleNavigation());

    return () => {
      if (window) window.removeEventListener('scroll', () => handleNavigation());
    };
  }, []);

  return (
    <IntroductionSectionStyle bannerIndex={bannerIndex}>
      <div className="sticky">
        <div className="frame">
          <Box className="banner image">
            <span role="img" aria-label={t(`banner0${bannerIndex + 1}_alt`)} />
            <Box className="container">
              {bannerIndex === 0 && (
                <MoveUp ratio={scrollRatio + initRatio}>
                  <img className="logo" src="/static/icons/skl-bu-logo.svg" alt={t('skilllane_b2b')} />
                  <Typography fontSize={36} className="sub-header">
                    {t('skilllane_b2b')}
                  </Typography>
                  <Typography fontSize={48} className="header">
                    {t('course_number_one_in_thailand')}
                  </Typography>
                  <Typography fontSize={36} className="sub-header">
                    {t('for_organization')}
                  </Typography>
                  <FrameGroup frames={FRAME_DATA_GROUP[bannerIndex]} />
                </MoveUp>
              )}
              {bannerIndex === 1 && (
                <MoveUp ratio={scrollRatio}>
                  <Typography fontSize={36} className="sub-header">
                    {t('high_performance_system')}
                  </Typography>
                  <Typography fontSize={48} className="header">
                    {t('help_hr')}
                  </Typography>
                  <Typography fontSize={36} className="sub-header">
                    {t('develop_organization')}
                  </Typography>
                  <FrameGroup frames={FRAME_DATA_GROUP[bannerIndex]} />
                </MoveUp>
              )}
              {bannerIndex === 2 && (
                <MoveUp ratio={Math.min(scrollRatio, 0.6)}>
                  <Typography fontSize={36} className="sub-header">
                    {t('expert_team')}
                  </Typography>
                  <Typography fontSize={48} className="header">
                    {t('skilllane_care')}
                  </Typography>
                  <Typography fontSize={36} className="sub-header">
                    {t('professional_consult')}
                  </Typography>
                  <FrameGroup frames={FRAME_DATA_GROUP[bannerIndex]} />
                </MoveUp>
              )}
            </Box>
          </Box>
        </div>
      </div>
    </IntroductionSectionStyle>
  );
};

export default IntroductionSection;
